import type { StringTable } from '../index.ts';

/**
 * Shell screens (namespace "shell"): Start, Game over, Leaderboards, Settings,
 * Onboarding / Profile, the parental gate, the store, the rewarded sample ad
 * and the Watch preview. EN and AF must have identical keys and placeholders
 * (test/i18n.test.ts), and every key must be used somewhere in the shell.
 *
 * Afrikaans is South African Afrikaans as children hear it at school:
 * "ranglys" (leaderboard), "bynaam" (nickname), "somme" (sums), "rondte" (run).
 */
export const SHELL: StringTable = {
  en: {
    // ───────── Start & navigation
    startTagline: 'Solve the sum before it lands!',
    startMenuLabel: 'Main menu',
    startPlay: 'Play',
    startGettingReady: 'Getting ready…',
    startPlayHint: 'The game picks the right level for you.',
    startOffline: 'Offline: your scores stay on this device',
    navLeaderboards: 'Leaderboards',
    navSettings: 'Settings',
    navProfile: 'Profile',
    navHome: 'Home',

    // ───────── Game over
    goHeadlineSuper: 'Superstar!',
    goHeadlineGreat: 'Great run!',
    goHeadlineNice: 'Nice try!',
    goHeadlineQuit: 'Run over',
    goHeadlineEmpty: 'Play again when you’re ready!',
    goPersonalBest: 'New personal best!',
    goScoresLabel: 'Your scores',
    goRaw: 'Arcade score',
    goRawNote: 'Points from this run',
    goRated: 'Rated score',
    goRatedNote: 'Fair for every grade',
    goStatsLabel: 'How you did',
    goAccuracy: 'Right answers',
    goCorrectOf: '{correct} of {total} right',
    goMedianTime: 'Time per sum',
    goLevels: 'Levels',
    goReached: 'Highest level',
    goLayers: 'Layers broken',
    goStreak: 'Most right in a row',
    goJourney: 'Your level journey',
    goJourneyLabel: 'Levels during the run: {levels}',
    goJourneyEmpty: 'Answer {n} sums to see your level journey.',
    goNoAnswers: 'No sums answered this time, so there is nothing to save.',
    goSaving: 'Saving your score…',
    goRanksTitle: 'Your place on the leaderboards',
    goSaved: 'Score saved!',
    goJoinTitle: 'Join the leaderboard?',
    goJoinBody: 'Pick a nickname and your school to see how you rank against other players. It only takes a minute.',
    goJoinOptIn: 'Turn on the leaderboard to show this score to other players.',
    goJoinButton: 'Join the leaderboard',
    goJoinLater: 'Not now',
    goJoinQuiet: 'Want to see how you rank?',
    goPlacementTitle: 'Your level is ready!',
    goPlacement: 'This first run found the right level for you. Your next run counts for the leaderboard!',
    goPlacementJoin: 'Join the leaderboard now, so your next run shows up there.',
    goReview: 'Your score is saved. We’re double-checking this run before it shows on the leaderboard.',
    goRelaxedTitle: 'You played with more time',
    goRelaxed: 'Your score and level are saved. More time runs don’t go on the leaderboard, so take all the time you need!',
    goOffline: 'You played offline, so this run can’t go on the leaderboard. Your level is saved on this device.',
    goSubmitError: 'Something went wrong while saving your score.',
    goRefusedExpired: 'This run took too long to send, so it can’t be ranked.',
    goRefusedUsed: 'This run was already sent.',
    goRefusedVersion: 'Please reload the game to get the newest version. Then your runs can be ranked again.',
    goRefusedCheck: 'This run couldn’t be checked, so it isn’t on the leaderboard.',
    goPlayAgain: 'Play again',

    // ───────── Leaderboards
    lbTitle: 'Leaderboards',
    lbDemoBadge: 'Offline demo leaderboard',
    lbDemoNote: 'Offline demo: these players are made up, and your scores stay on this device.',
    lbMetric: 'Score type',
    lbRated: 'Rated',
    lbArcade: 'Arcade',
    lbRatedNote: 'Rated scores are fair for every grade: they show how well you play at your own level.',
    lbArcadeNote: 'Arcade scores add up points: harder levels and longer runs score more.',
    lbScope: 'Show players from',
    scopeGlobal: 'Everyone',
    scopeCountry: 'Country',
    scopeProvince: 'Province',
    scopeCity: 'Town or city',
    scopeSchool: 'School',
    scopeGrade: 'My grade',
    lbGradeFilter: 'Grade',
    lbAllGrades: 'All grades',
    lbMyGrade: 'My grade',
    lbPeriod: 'Time',
    lbAllTime: 'All time',
    lbThisWeek: 'This week',
    lbLoading: 'Loading the leaderboard…',
    lbError: 'Couldn’t load the leaderboard. Check your connection.',
    lbEmpty: 'No scores here yet. Be the first!',
    lbNeedsProfile: 'Join the leaderboard with your school and town to see this board.',
    lbNeedsGrade: 'Pick a grade above to see this board.',
    lbNotOnBoards: 'You’re not on the leaderboards yet.',
    lbTotal: '{count} players',
    lbPodium: 'Top three',
    lbList: 'Ranking',
    lbRankN: 'Place {rank}',
    lbYou: 'You',

    // ───────── Settings
    setTitle: 'Settings',
    setLanguage: 'Language',
    setSound: 'Sound',
    setMute: 'Mute all sound',
    setMuteDesc: 'Music and effects stay quiet.',
    setVolumeMaster: 'Volume',
    setVolumeMusic: 'Music',
    setVolumeEffects: 'Sound effects',
    setInput: 'How to answer',
    setInputAuto: 'Automatic',
    setInputAutoDesc: 'Handwriting on touch screens once you’ve tried it, choices everywhere else.',
    setInputChoices: 'Multiple choice',
    setInputChoicesDesc: 'Tap one of four answers.',
    setInputHandwriting: 'Handwriting',
    setInputHandwritingDesc: 'Write the answer with your finger or a pen.',
    setCalibrated: 'Dropstack knows {count} samples of your handwriting.',
    setNotCalibrated: 'Teach Dropstack your handwriting so it reads your numbers better.',
    setCalibrate: 'Handwriting practice',
    setHelpTitle: 'Handwriting tips',
    setHelpWrite: 'Write your answer big and clear, one digit after the other from left to right.',
    setHelpFix: 'If a digit is read wrong, tap it to change it.',
    setHelpChoices: 'Stuck? Tap “Choices” to pick from four answers instead.',
    setHelpKeyboard: 'On a computer you can also type your answer and press Enter.',
    setHelpPrivate: 'Your handwriting stays on this device. It is never uploaded.',
    setAccessibility: 'Easier to see and use',
    setLargeText: 'Larger text',
    setLargeTextDesc: 'Makes words and buttons bigger.',
    setReducedMotion: 'Less movement',
    setReducedMotionDesc: 'Turns off bouncing and moving backgrounds.',
    setHighContrast: 'High contrast',
    setHighContrastDesc: 'Solid panels and stronger colours for easier reading.',
    setLeftHanded: 'Left-handed',
    setLeftHandedDesc: 'Puts the answer buttons on the left when the screen is sideways.',
    setRelaxedPace: 'More time',
    setRelaxedPaceDesc: 'Sums fall half as fast, so you get twice the time (from your next run). More time runs don’t go on the leaderboard.',
    setPrivacy: 'Privacy',
    setPrivacyPublic: 'If you join the leaderboard, other players can see:',
    setPrivacyItemName: 'your nickname',
    setPrivacyItemSchool: 'your grade, school, town and province',
    setPrivacyItemScores: 'your best scores and the highest level you reached',
    setPrivacyNever: 'We never show your real name, your age or where you live.',
    setOptIn: 'Show me on the leaderboards',
    setOptInDesc: 'Off means your scores are kept private.',
    setOptInNeedsProfile: 'Pick a nickname and your school first.',
    setOptInFailed: 'Couldn’t save that right now. Please try again.',
    setPurchases: 'Shop',
    setNoPurchases: 'You haven’t bought anything.',
    setAdsRemoved: 'Ads are removed. Thank you!',
    setPlusActive: 'Dropstack Plus is active. Thank you!',
    setPlusUntil: 'Dropstack Plus is active until {date}.',
    setStore: 'Remove ads or get Dropstack Plus',
    setRestore: 'Restore purchases',
    setGateNote: 'A grown-up needs to help with these.',
    setRestored: 'Your purchases are back.',
    setRestoreNothing: 'There were no purchases to restore.',
    setWatch: 'Apple Watch preview',
    setWatchToggle: 'Show the Watch layout',
    setWatchDesc: 'See how the game looks on a 45 mm Apple Watch.',
    setReset: 'Start over',
    setResetDesc: 'Start again at level 1 as a new player on this device.',
    setResetButton: 'Reset progress',
    setResetConfirmTitle: 'Reset your progress?',
    setResetConfirmBody:
      'This device will forget your player — your level, nickname, school and scores — and you’ll start again at level 1. Your stickers, purchases, settings and handwriting practice stay.',
    setResetDone: 'Done! You’re starting fresh at level 1.',

    // ───────── Parental gate
    gateTitle: 'Ask a grown-up',
    gateIntro: 'This part is for parents and guardians.',
    gateHold: 'Step 1: press and hold the button until it is full.',
    gateHoldButton: 'Press and hold',
    gateType: 'Step 2: type this number in digits.',
    gateCheck: 'Check',
    gateWrong: 'That’s not it. Ask a grown-up to help. Here is a new number.',
    gateLocked: 'Too many tries. Please wait a minute, then ask a grown-up to try again.',

    // ───────── Store
    storeTitle: 'Dropstack store',
    storeTestNote: 'Test purchase — no money is charged.',
    storePlusTitle: 'What Dropstack Plus includes',
    storePerkNoAds: 'No ads',
    storePerkPractice: 'Extra practice packs',
    storePerkThemes: 'Bonus themes for your profile',
    storePerkReport: 'A progress report for parents',
    storeComingSoon: 'Coming soon',
    storeFair: 'Nothing you buy changes a ranked run: everyone plays the same game.',
    storeRemoveAds: 'Remove ads',
    storePlusMonthly: 'Dropstack Plus (monthly)',
    storePlusYearly: 'Dropstack Plus (yearly)',
    storePriceOnce: '{price}, once',
    storePricePerMonth: '{price} per month',
    storePricePerYear: '{price} per year',
    storeRenews: 'Renews automatically. Cancel any time in the App Store or Google Play.',
    storeBuy: 'Buy',
    storeBuyTest: 'Buy (test)',
    storeOwned: 'Owned',
    storeIncludedInPlus: 'Included in Plus',
    storeActiveUntil: 'Active until {date}',
    storeThanks: 'Thank you! Your purchase is ready.',
    storeCancelled: 'Purchase cancelled. Nothing was charged.',
    storeFailed: 'That didn’t work. Nothing was charged.',

    // ───────── Rewarded sample ad
    rewardBody: 'In the real app, a short, child-safe ad would play here. You can close it any time.',
    rewardProgress: 'Sample ad progress',
    rewardCollect: 'Collect my sticker',

    // ───────── Profile & stickers
    profTitle: 'Profile',
    profGrade: 'Grade',
    profSchool: 'School',
    profPlace: 'Where',
    profBoards: 'Leaderboards',
    profBoardsOn: 'Showing my scores',
    profBoardsOff: 'Private',
    profEdit: 'Edit profile',
    profEditField: 'Change {field}',
    profNewTitle: 'Make it yours',
    profNewText: 'Choose a fun nickname and your school to join the leaderboards. You can play without it too.',
    profThemes: 'Themes',
    profThemesCount: '{count} of {total} unlocked',
    profThemesHint: 'Reach a new level in a run to unlock its theme.',
    profThemeLocked: 'Locked',
    profStickers: 'Theme stickers',
    profStickerCount: '{count} of {total} collected',
    profEarnSticker: 'Watch an ad to earn a sticker',
    profEarnNote: 'Stickers are just for fun: they never change the game.',
    profStickerEarned: 'You earned the {name} sticker!',
    profStickerUnavailable: 'No sticker ads right now. Try again later.',
    profStickerTomorrow: 'You’ve collected today’s stickers. Keep playing to unlock more themes!',
    profStickersReachMore: 'You have a sticker for every theme you’ve unlocked. Reach a new level to unlock more!',
    profAllStickers: 'You’ve collected every sticker. Amazing!',
    stickerLocked: 'Level {level} sticker, not collected yet',

    // ───────── Onboarding
    obJoinTitle: 'Join the leaderboard',
    obEditTitle: 'Edit profile',
    obSkip: 'Skip for now',
    obProgress: 'Step {step} of {total}',
    obFinish: 'Finish',
    obNameTitle: 'Pick a nickname',
    obNameText: 'This is the name other players see on the leaderboards.',
    obNameWarning: 'Don’t use your real full name. A fun nickname keeps you safe online.',
    obNameLabel: 'Nickname',
    obNameIdeas: 'Need an idea? Tap one:',
    obNameMore: 'More ideas',
    obNameTooShort: 'Use at least 3 letters or numbers.',
    obNameTooLong: 'Use 20 characters or fewer.',
    obNameChars: 'Use only letters, numbers and spaces.',
    obNameNoLetters: 'Put at least one letter in your nickname.',
    obNameDigits: 'Use at most 4 numbers in a nickname.',
    obNameNotAllowed: 'That name can’t be used. Try another one!',
    obNameInvalid: 'Please choose a different nickname.',
    obGradeTitle: 'Which grade are you in?',
    obGradeText: 'This puts you on your grade’s leaderboard. The game still picks your level by how you play.',
    obGradeWord: 'Grade',
    obNeedGrade: 'Tap your grade.',
    obSchoolTitle: 'Your school',
    obSchoolText: 'Find your school so you can see how you rank against your classmates.',
    obCountry: 'Country',
    obProvince: 'Province',
    obProvinceFree: 'Province or state (optional)',
    obCity: 'Town or city',
    obCityHint: 'Pick one below or type it.',
    obCitySuggestions: 'Suggested towns',
    obSchool: 'School name',
    obSchoolHint: 'Start typing to search.',
    obSchoolPicked: 'School chosen',
    obSchoolResults: 'Schools found',
    obSchoolAdd: 'Can’t find it? “{name}” will be added as a new school when you finish.',
    obNeedCountry: 'Choose your country.',
    obNeedProvince: 'Tap your province.',
    obNeedCity: 'Type your town or city.',
    obNeedSchool: 'Type the name of your school.',
    obPlaceChars: 'Use only letters, numbers, spaces and simple marks like . , - or ’.',
    obPlaceNotAllowed: 'That name can’t be used. Please check it and try again.',
    obBoardTitle: 'Show your scores?',
    obBoardText: 'If you say yes, other players can see:',
    obBoardParent: 'If you are under 18, ask a parent or guardian before you join.',
    obBoardYes: 'Yes, show my scores',
    obBoardYesDesc: 'Your nickname and best scores appear on the leaderboards.',
    obBoardNo: 'No thanks',
    obBoardNoDesc: 'Keep my scores private. I can change this later in Settings.',
    obSaveFailed: 'Couldn’t save your profile. Please try again.',
    obSaveOffline: 'You’re offline right now. Connect to the internet and try again.',

    // ───────── New version
    updateAvailable: 'A new version of Dropstack is ready. Reload to get it, so your runs can go on the leaderboard again.',
    updateReload: 'Reload',

    // ───────── Watch preview
    watchCaption: 'Apple Watch preview (45 mm)',
    watchExit: 'Leave Watch preview',
    watchStartLabel: 'Dropstack on Apple Watch',
    watchOver: 'Run over!',
    watchBest: 'Best ever!',
    watchAgain: 'Again',
  },
  af: {
    // ───────── Start & navigation
    startTagline: 'Los die som op voor dit land!',
    startMenuLabel: 'Hoofkieslys',
    startPlay: 'Speel',
    startGettingReady: 'Maak gereed…',
    startPlayHint: 'Die speletjie kies die regte vlak vir jou.',
    startOffline: 'Vanlyn: jou tellings bly op hierdie toestel',
    navLeaderboards: 'Ranglyste',
    navSettings: 'Instellings',
    navProfile: 'Profiel',
    navHome: 'Tuis',

    // ───────── Game over
    goHeadlineSuper: 'Superster!',
    goHeadlineGreat: 'Puik gespeel!',
    goHeadlineNice: 'Mooi probeer!',
    goHeadlineQuit: 'Rondte verby',
    goHeadlineEmpty: 'Speel weer wanneer jy reg is!',
    goPersonalBest: 'Nuwe persoonlike rekord!',
    goScoresLabel: 'Jou tellings',
    goRaw: 'Arkadetelling',
    goRawNote: 'Punte in hierdie rondte',
    goRated: 'Billike telling',
    goRatedNote: 'Regverdig vir elke graad',
    goStatsLabel: 'Hoe dit gegaan het',
    goAccuracy: 'Regte antwoorde',
    goCorrectOf: '{correct} uit {total} reg',
    goMedianTime: 'Tyd per som',
    goLevels: 'Vlakke',
    goReached: 'Hoogste vlak',
    goLayers: 'Lae gebreek',
    goStreak: 'Meeste reg na mekaar',
    goJourney: 'Jou reis deur die vlakke',
    goJourneyLabel: 'Vlakke in die rondte: {levels}',
    goJourneyEmpty: 'Beantwoord {n} somme om jou reis deur die vlakke te sien.',
    goNoAnswers: 'Geen somme is hierdie keer beantwoord nie, so daar is niks om te stoor nie.',
    goSaving: 'Stoor jou telling…',
    goRanksTitle: 'Jou plek op die ranglyste',
    goSaved: 'Telling gestoor!',
    goJoinTitle: 'Sluit by die ranglys aan?',
    goJoinBody: 'Kies ’n bynaam en jou skool om te sien hoe jy teen ander spelers vaar. Dit vat net ’n minuut.',
    goJoinOptIn: 'Skakel die ranglys aan om hierdie telling vir ander spelers te wys.',
    goJoinButton: 'Sluit by die ranglys aan',
    goJoinLater: 'Nie nou nie',
    goJoinQuiet: 'Wil jy sien waar jy staan?',
    goPlacementTitle: 'Jou vlak is gereed!',
    goPlacement: 'Hierdie eerste rondte het die regte vlak vir jou gevind. Jou volgende rondte tel vir die ranglys!',
    goPlacementJoin: 'Sluit nou by die ranglys aan, dan verskyn jou volgende rondte daar.',
    goReview: 'Jou telling is gestoor. Ons gaan hierdie rondte eers weer na voordat dit op die ranglys verskyn.',
    goRelaxedTitle: 'Jy het met meer tyd gespeel',
    goRelaxed: 'Jou telling en vlak is gestoor. Meer tyd-rondtes kom nie op die ranglys nie, so vat gerus al die tyd wat jy nodig het!',
    goOffline: 'Jy het vanlyn gespeel, so hierdie rondte kan nie op die ranglys kom nie. Jou vlak is op hierdie toestel gestoor.',
    goSubmitError: 'Iets het skeefgeloop met die stoor van jou telling.',
    goRefusedExpired: 'Dit het te lank gevat om hierdie rondte te stuur, so dit kan nie op die ranglys kom nie.',
    goRefusedUsed: 'Hierdie rondte is reeds gestuur.',
    goRefusedVersion: 'Laai asseblief die speletjie weer om die nuutste weergawe te kry. Dan kan jou rondtes weer op die ranglys kom.',
    goRefusedCheck: 'Hierdie rondte kon nie nagegaan word nie, so dit is nie op die ranglys nie.',
    goPlayAgain: 'Speel weer',

    // ───────── Leaderboards
    lbTitle: 'Ranglyste',
    lbDemoBadge: 'Vanlyn-demoranglys',
    lbDemoNote: 'Vanlyn-demo: hierdie spelers is opgemaak, en jou tellings bly op hierdie toestel.',
    lbMetric: 'Soort telling',
    lbRated: 'Billik',
    lbArcade: 'Arkade',
    lbRatedNote: 'Billike tellings is regverdig vir elke graad: dit wys hoe goed jy op jou eie vlak speel.',
    lbArcadeNote: 'Arkadetellings tel punte bymekaar: moeiliker vlakke en langer rondtes kry meer.',
    lbScope: 'Wys spelers van',
    scopeGlobal: 'Almal',
    scopeCountry: 'Land',
    scopeProvince: 'Provinsie',
    scopeCity: 'Dorp of stad',
    scopeSchool: 'Skool',
    scopeGrade: 'My graad',
    lbGradeFilter: 'Graad',
    lbAllGrades: 'Alle grade',
    lbMyGrade: 'My graad',
    lbPeriod: 'Tydperk',
    lbAllTime: 'Nog altyd',
    lbThisWeek: 'Hierdie week',
    lbLoading: 'Laai die ranglys…',
    lbError: 'Kon nie die ranglys laai nie. Kyk of jy aanlyn is.',
    lbEmpty: 'Nog geen tellings hier nie. Wees die eerste!',
    lbNeedsProfile: 'Sluit met jou skool en dorp by die ranglys aan om hierdie lys te sien.',
    lbNeedsGrade: 'Kies hierbo ’n graad om hierdie lys te sien.',
    lbNotOnBoards: 'Jy is nog nie op die ranglyste nie.',
    lbTotal: '{count} spelers',
    lbPodium: 'Top drie',
    lbList: 'Rangorde',
    lbRankN: 'Plek {rank}',
    lbYou: 'Jy',

    // ───────── Settings
    setTitle: 'Instellings',
    setLanguage: 'Taal',
    setSound: 'Klank',
    setMute: 'Demp alle klank',
    setMuteDesc: 'Musiek en klankeffekte bly stil.',
    setVolumeMaster: 'Volume',
    setVolumeMusic: 'Musiek',
    setVolumeEffects: 'Klankeffekte',
    setInput: 'Hoe om te antwoord',
    setInputAuto: 'Outomaties',
    setInputAutoDesc: 'Handskrif op raakskerms sodra jy dit probeer het, andersins keuses.',
    setInputChoices: 'Meervoudige keuse',
    setInputChoicesDesc: 'Tik op een van vier antwoorde.',
    setInputHandwriting: 'Handskrif',
    setInputHandwritingDesc: 'Skryf die antwoord met jou vinger of ’n pen.',
    setCalibrated: 'Dropstack ken {count} voorbeelde van jou handskrif.',
    setNotCalibrated: 'Leer Dropstack jou handskrif sodat dit jou syfers beter kan lees.',
    setCalibrate: 'Handskrifoefening',
    setHelpTitle: 'Handskrifwenke',
    setHelpWrite: 'Skryf jou antwoord groot en duidelik, een syfer na die ander van links na regs.',
    setHelpFix: 'As ’n syfer verkeerd gelees word, tik daarop om dit te verander.',
    setHelpChoices: 'Vas? Tik op “Keuses” om eerder uit vier antwoorde te kies.',
    setHelpKeyboard: 'Op ’n rekenaar kan jy ook jou antwoord intik en Enter druk.',
    setHelpPrivate: 'Jou handskrif bly op hierdie toestel. Dit word nooit opgelaai nie.',
    setAccessibility: 'Makliker om te sien en te gebruik',
    setLargeText: 'Groter teks',
    setLargeTextDesc: 'Maak woorde en knoppies groter.',
    setReducedMotion: 'Minder beweging',
    setReducedMotionDesc: 'Skakel wipbewegings en bewegende agtergronde af.',
    setHighContrast: 'Hoë kontras',
    setHighContrastDesc: 'Soliede panele en sterker kleure wat makliker is om te lees.',
    setLeftHanded: 'Linkshandig',
    setLeftHandedDesc: 'Sit die antwoordknoppies links as die skerm dwars gedraai is.',
    setRelaxedPace: 'Meer tyd',
    setRelaxedPaceDesc: 'Somme val half so vinnig, so jy kry dubbel die tyd (vanaf jou volgende rondte). Meer tyd-rondtes kom nie op die ranglys nie.',
    setPrivacy: 'Privaatheid',
    setPrivacyPublic: 'As jy by die ranglys aansluit, kan ander spelers sien:',
    setPrivacyItemName: 'jou bynaam',
    setPrivacyItemSchool: 'jou graad, skool, dorp en provinsie',
    setPrivacyItemScores: 'jou beste tellings en die hoogste vlak wat jy bereik het',
    setPrivacyNever: 'Ons wys nooit jou regte naam, jou ouderdom of waar jy woon nie.',
    setOptIn: 'Wys my op die ranglyste',
    setOptInDesc: 'Af beteken jou tellings bly privaat.',
    setOptInNeedsProfile: 'Kies eers ’n bynaam en jou skool.',
    setOptInFailed: 'Kon dit nie nou stoor nie. Probeer asseblief weer.',
    setPurchases: 'Winkel',
    setNoPurchases: 'Jy het nog niks gekoop nie.',
    setAdsRemoved: 'Advertensies is verwyder. Dankie!',
    setPlusActive: 'Dropstack Plus is aktief. Dankie!',
    setPlusUntil: 'Dropstack Plus is aktief tot {date}.',
    setStore: 'Verwyder advertensies of kry Dropstack Plus',
    setRestore: 'Herstel aankope',
    setGateNote: '’n Grootmens moet hiermee help.',
    setRestored: 'Jou aankope is terug.',
    setRestoreNothing: 'Daar was geen aankope om te herstel nie.',
    setWatch: 'Apple Watch-voorskou',
    setWatchToggle: 'Wys die horlosie-uitleg',
    setWatchDesc: 'Kyk hoe die speletjie op ’n Apple Watch van 45 mm lyk.',
    setReset: 'Begin van voor af',
    setResetDesc: 'Begin weer by vlak 1 as ’n nuwe speler op hierdie toestel.',
    setResetButton: 'Vee vordering uit',
    setResetConfirmTitle: 'Vee jou vordering uit?',
    setResetConfirmBody:
      'Hierdie toestel sal jou speler vergeet — jou vlak, bynaam, skool en tellings — en jy begin weer by vlak 1. Jou plakkers, aankope, instellings en handskrifoefening bly.',
    setResetDone: 'Klaar! Jy begin nou weer vars by vlak 1.',

    // ───────── Parental gate
    gateTitle: 'Vra ’n grootmens',
    gateIntro: 'Hierdie deel is vir ouers en voogde.',
    gateHold: 'Stap 1: druk en hou die knoppie totdat dit vol is.',
    gateHoldButton: 'Druk en hou',
    gateType: 'Stap 2: tik hierdie getal in syfers.',
    gateCheck: 'Kontroleer',
    gateWrong: 'Dis nie reg nie. Vra ’n grootmens om te help. Hier is ’n nuwe getal.',
    gateLocked: 'Te veel pogings. Wag asseblief ’n minuut en vra dan ’n grootmens om weer te probeer.',

    // ───────── Store
    storeTitle: 'Dropstack-winkel',
    storeTestNote: 'Toetsaankoop — geen geld word gehef nie.',
    storePlusTitle: 'Wat Dropstack Plus insluit',
    storePerkNoAds: 'Geen advertensies',
    storePerkPractice: 'Ekstra oefenpakke',
    storePerkThemes: 'Bonustemas vir jou profiel',
    storePerkReport: '’n Vorderingsverslag vir ouers',
    storeComingSoon: 'Kom binnekort',
    storeFair: 'Niks wat jy koop, verander ’n rondte vir die ranglys nie: almal speel dieselfde speletjie.',
    storeRemoveAds: 'Verwyder advertensies',
    storePlusMonthly: 'Dropstack Plus (maandeliks)',
    storePlusYearly: 'Dropstack Plus (jaarliks)',
    storePriceOnce: '{price}, eenmalig',
    storePricePerMonth: '{price} per maand',
    storePricePerYear: '{price} per jaar',
    storeRenews: 'Hernu outomaties. Kanselleer enige tyd in die App Store of Google Play.',
    storeBuy: 'Koop',
    storeBuyTest: 'Koop (toets)',
    storeOwned: 'Reeds gekoop',
    storeIncludedInPlus: 'Ingesluit by Plus',
    storeActiveUntil: 'Aktief tot {date}',
    storeThanks: 'Dankie! Jou aankoop is gereed.',
    storeCancelled: 'Aankoop gekanselleer. Niks is gehef nie.',
    storeFailed: 'Dit het nie gewerk nie. Niks is gehef nie.',

    // ───────── Rewarded sample ad
    rewardBody: 'In die regte toep sou ’n kort, kindervriendelike advertensie hier speel. Jy kan dit enige tyd toemaak.',
    rewardProgress: 'Vordering van die voorbeeldadvertensie',
    rewardCollect: 'Kry my plakker',

    // ───────── Profile & stickers
    profTitle: 'Profiel',
    profGrade: 'Graad',
    profSchool: 'Skool',
    profPlace: 'Waar',
    profBoards: 'Ranglyste',
    profBoardsOn: 'Wys my tellings',
    profBoardsOff: 'Privaat',
    profEdit: 'Wysig profiel',
    profEditField: 'Verander {field}',
    profNewTitle: 'Maak dit joune',
    profNewText: 'Kies ’n prettige bynaam en jou skool om by die ranglyste aan te sluit. Jy kan ook sonder dit speel.',
    profThemes: 'Temas',
    profThemesCount: '{count} van {total} oopgesluit',
    profThemesHint: 'Bereik ’n nuwe vlak in ’n rondte om sy tema oop te sluit.',
    profThemeLocked: 'Gesluit',
    profStickers: 'Temaplakkers',
    profStickerCount: '{count} van {total} versamel',
    profEarnSticker: 'Kyk ’n advertensie en verdien ’n plakker',
    profEarnNote: 'Plakkers is net vir die pret: hulle verander nooit die speletjie nie.',
    profStickerEarned: 'Jy het die {name}-plakker verdien!',
    profStickerUnavailable: 'Geen plakkeradvertensies nou nie. Probeer later weer.',
    profStickerTomorrow: 'Jy het vandag se plakkers gekry. Speel verder om meer temas oop te sluit!',
    profStickersReachMore: 'Jy het ’n plakker vir elke tema wat jy oopgesluit het. Bereik ’n nuwe vlak om meer oop te sluit!',
    profAllStickers: 'Jy het elke plakker versamel. Fantasties!',
    stickerLocked: 'Plakker vir vlak {level}, nog nie versamel nie',

    // ───────── Onboarding
    obJoinTitle: 'Sluit by die ranglys aan',
    obEditTitle: 'Wysig profiel',
    obSkip: 'Slaan vir eers oor',
    obProgress: 'Stap {step} van {total}',
    obFinish: 'Klaar',
    obNameTitle: 'Kies ’n bynaam',
    obNameText: 'Dis die naam wat ander spelers op die ranglyste sien.',
    obNameWarning: 'Moenie jou regte volle naam gebruik nie. ’n Prettige bynaam hou jou veilig aanlyn.',
    obNameLabel: 'Bynaam',
    obNameIdeas: 'Soek jy ’n idee? Tik op een:',
    obNameMore: 'Nog idees',
    obNameTooShort: 'Gebruik ten minste 3 letters of syfers.',
    obNameTooLong: 'Gebruik 20 karakters of minder.',
    obNameChars: 'Gebruik net letters, syfers en spasies.',
    obNameNoLetters: 'Sit ten minste een letter in jou bynaam.',
    obNameDigits: 'Gebruik hoogstens 4 syfers in ’n bynaam.',
    obNameNotAllowed: 'Daardie naam werk nie. Probeer ’n ander een!',
    obNameInvalid: 'Kies asseblief ’n ander bynaam.',
    obGradeTitle: 'In watter graad is jy?',
    obGradeText: 'Dit plaas jou op jou graad se ranglys. Die speletjie kies steeds jou vlak volgens hoe jy speel.',
    obGradeWord: 'Graad',
    obNeedGrade: 'Tik op jou graad.',
    obSchoolTitle: 'Jou skool',
    obSchoolText: 'Soek jou skool sodat jy kan sien hoe jy teen jou klasmaats vaar.',
    obCountry: 'Land',
    obProvince: 'Provinsie',
    obProvinceFree: 'Provinsie of staat (opsioneel)',
    obCity: 'Dorp of stad',
    obCityHint: 'Kies een hieronder of tik dit in.',
    obCitySuggestions: 'Voorgestelde dorpe',
    obSchool: 'Naam van jou skool',
    obSchoolHint: 'Begin tik om te soek.',
    obSchoolPicked: 'Skool gekies',
    obSchoolResults: 'Skole gevind',
    obSchoolAdd: 'Kan jy dit nie kry nie? “{name}” word as ’n nuwe skool bygevoeg wanneer jy klaar is.',
    obNeedCountry: 'Kies jou land.',
    obNeedProvince: 'Tik op jou provinsie.',
    obNeedCity: 'Tik jou dorp of stad in.',
    obNeedSchool: 'Tik die naam van jou skool in.',
    obPlaceChars: 'Gebruik net letters, syfers, spasies en eenvoudige tekens soos . , - of ’.',
    obPlaceNotAllowed: 'Daardie naam kan nie gebruik word nie. Kyk dit asseblief na en probeer weer.',
    obBoardTitle: 'Wys jou tellings?',
    obBoardText: 'As jy ja sê, kan ander spelers sien:',
    obBoardParent: 'As jy jonger as 18 is, vra ’n ouer of voog voordat jy aansluit.',
    obBoardYes: 'Ja, wys my tellings',
    obBoardYesDesc: 'Jou bynaam en beste tellings verskyn op die ranglyste.',
    obBoardNo: 'Nee dankie',
    obBoardNoDesc: 'Hou my tellings privaat. Ek kan dit later in Instellings verander.',
    obSaveFailed: 'Kon nie jou profiel stoor nie. Probeer asseblief weer.',
    obSaveOffline: 'Jy is nou vanlyn. Koppel aan die internet en probeer weer.',

    // ───────── Nuwe weergawe
    updateAvailable: '’n Nuwe weergawe van Dropstack is gereed. Laai die bladsy weer, dan kan jou rondtes weer op die ranglys kom.',
    updateReload: 'Laai weer',

    // ───────── Watch preview
    watchCaption: 'Apple Watch-voorskou (45 mm)',
    watchExit: 'Verlaat horlosievoorskou',
    watchStartLabel: 'Dropstack op Apple Watch',
    watchOver: 'Rondte verby!',
    watchBest: 'Beste ooit!',
    watchAgain: 'Weer',
  },
};
